import { Filter } from "@/dto/Pageable";
import InvoiceDTO, { InvoiceItem } from "@/dto/InvoiceDTO";
import AddressDTO from "@/dto/AddressDTO";
import { IProfile } from "@/dto/IProfile";
import BranchDto, { Branch } from "@/components/profile/branches/BranchDtos";
import { ProjectDTO } from "@/dto/payroll/ProjectDTOs";
import { AccountType } from "@/constants/AccountTypes";
import moment from "moment/moment";
import FileMetaDTO from "@/dto/files/FileMetaDTO";
import { PaymentDTO } from "@/dto/payment/PaymentDTO";

export interface ServiceDTO {
    id: number | null;
    name: string;
    description?: string | null;
    price: number;
    formattedPrice: string;
    salable: boolean;
    schedule: boolean;
    salesRate?: number;
    active: boolean;
    coefficient: string | null
    holder: IProfile
}

export class SelectableServiceLogDTO implements ServiceRecordDTO {
    id!: number;
    serviceId!: number | null;
    title!: string;
    unitAmount!: number;
    formattedPrice!: string;
    discount: number;
    executor!: IProfile;
    recipient!: IProfile
    location!: string;
    state!: string;
    salable!: boolean;
    schedule!: boolean;
    salesRate!: number;
    active!: boolean;
    description?: string;
    startDate: string | null = null
    endDate: string | null = null;
    total!: number
    formattedTotal!: string;
    formattedSalesRate!: string;
    selected = false;
    quantity!: number;
    branchId!: number | null;
    addressId!: number | null;
    projectId!: number | null;

    constructor(service: ServiceRecordDTO) {
        this.id = service.id
        this.serviceId = service.serviceId
        this.title = service.title
        this.unitAmount = service.unitAmount
        this.formattedPrice = service.formattedPrice
        this.discount = service.discount
        this.executor = service.executor
        this.recipient = service.recipient
        this.location = service.location
        this.state = service.state
        this.salable = service.salable
        this.schedule = service.schedule
        this.salesRate = service.salesRate
        this.active = service.active
        this.description = service.description
        this.startDate = service.startDate
        this.total = service.total
        this.endDate = service.endDate
        this.formattedTotal = service.formattedTotal
        this.formattedSalesRate = service.formattedSalesRate
        this.quantity = service.quantity
        this.addressId = service.addressId
        this.branchId = service.branchId
        this.projectId = service.projectId
        this.gross = service.gross
        this.taxRate = service.taxRate
        this.taxType = service.taxType
    }

    gross!: number;
    taxRate!: number;
    taxType!: string;
}

export class ServiceFilter implements Filter {
    holderId: number | null = null;
    refId: number | null = null;
    name: string | null = null;
    salable: boolean | null = null;
    schedule: boolean | null = null;
    active: boolean | null = null;
    page = 0;
    size = 20;

    constructor(init?: Partial<ServiceFilter>) {
        Object.assign(this, init);
    }
}

export class CreateServicePayload {
    companyId: number | null = null;
    personId: number | null = null;
    name: string | null = null;
    description: string | null = null;
    price: number | null = null;
    taxExempt = false;
    scheduled = false;
    coefficient: number | null = null;

    constructor(init?: Partial<CreateServicePayload>) {
        Object.assign(this, init);
    }
}

export class UpdateServicePayload {
    serviceId: number | null = null;
    holderId: number | null = null;
    name: string | null = null;
    description: string | null = null;
    price: number | null = null;

    constructor(init?: Partial<UpdateServicePayload>) {
        Object.assign(this, init);
    }
}

export class ServiceRecordDTO extends InvoiceItem {
    id!: number;
    serviceId!: number | null;
    executor!: IProfile;
    recipient!: IProfile
    location!: string;
    state!: string;
    salable!: boolean;
    schedule!: boolean;
    salesRate!: number;
    active!: boolean;
    startDate: string | null = null
    endDate: string | null = null;
    addressId: number | null = null;
    branchId: number | null = null;
    projectId: number | null = null;
    invoice?: ShortServiceInvoiceDTO | null = null;

    formattedPrice!: string
    formattedTotal!: string;
    formattedSalesRate!: string;

}

export class ShortServiceInvoiceDTO {
    id!: number;
    invoiceNumber!: string;
    status!: string;
    total!: number;
    date!: string | Date;
    document!: FileMetaDTO;
}

export type InvoiceSearchMode = "REF" | "CREATOR"

export class InvoiceContactFilter {
    readonly createdBy: number | null = null;
    searchMode: InvoiceSearchMode = "REF"
    query: string | null = null;

    page = 0;
    size = 15;

    constructor(init?: Partial<InvoiceContactFilter>) {
        Object.assign(this, init);
    }
}

export class InvoiceContact implements IProfile {
    readonly id!: number
    readonly userId: number | null = null
    readonly tin: string | null = null
    readonly client: boolean | null = null
    readonly type: AccountType | null = null
    readonly phone: string | null = null
    //ref
    readonly name!: string
    readonly email!: string
    readonly address?: AddressDTO
    readonly ref?: number
    readonly refUserId?: number
    readonly refType?: string
    //ref

    readonly createdBy!: IProfile

    constructor(init?: Partial<InvoiceContact>) {
        Object.assign(this, init);
    }
}

export class ServiceRecordFilter {
    serviceId: number | null = null;
    location: string | null = null;
    active: boolean | null = null;
    startDate: Date | string | null = null;
    endDate: Date | string | null = null
    executorId: number | null = null;
    state: string | null = null;
    recipientId: number | null = null;
    onlyUninvoiced: boolean | null = null;
    page = 0;
    size = 20;

    constructor(init?: Partial<ServiceRecordFilter>) {
        Object.assign(this, init);
    }
}

export class UpdateServiceRecordPayload {
    id: number | null = null;
    serviceId: number | null = null;
    startDate: Date | string | null = null;
    endDate: Date | string | null = null;
    branchId: number | null = null;
    projectId: number | null = null;
    addressId: number | null = null;
    assigneeId: number | null = null;
    description: string | null = null;
    price: string | null = null
    quantity: number | null = null

    constructor(init?: Partial<UpdateServiceRecordPayload>) {
        Object.assign(this, init);
    }
}

export class CreateServiceRecordPayload {
    serviceId: number | null = null;
    title: string | null = null;
    startDate: Date | string | null = null;
    endDate: Date | string | null = null;
    coefficient: string | null = null;

    salesRate: number | null = null;
    branchId: number | null = null;
    projectId: number | null = null;
    addressId: number | null = null;

    executorId: number | null = null;
    executorCompanyId: number | null = null;
    executorPersonId: number | null = null;

    state: string | null = null;
    assigneeId: number | null = null;
    description: string | null = null;
    recipientId: number | null = null;

    price: string | null = null;
    quantity: number | null = null;
    taxExempt = false;

    constructor(init?: Partial<CreateServiceRecordPayload>) {
        Object.assign(this, init);
    }
}

export class UpdateAssigneePayload {
    assigneeId: number | null = null;
    companyId: number | null = null;
    serviceId: number | null = null;
}

export interface LocationOption {

    title(): string

    address(): string | null

    selected(req: CreateServiceRecordPayload | UpdateServiceRecordPayload): void
}

export class BranchLocationOption implements LocationOption {
    value: BranchDto

    title(): string {
        return this.value.title!
    }

    address(): string | null {
        if (this.value.address) {
            return this.value.address?.fullAddress
        } else {
            return null
        }
    }

    public selected(req: CreateServiceRecordPayload | UpdateServiceRecordPayload) {
        req.projectId = null
        req.branchId = this.value.id;
        req.addressId = null
    }

    constructor(value: BranchDto) {
        this.value = value
    }

    public static of(value: BranchDto): BranchLocationOption {
        return new BranchLocationOption(value)
    }
}

export class ProjectLocationOption implements LocationOption {
    value: ProjectDTO

    title(): string {
        return this.value.title
    }

    address(): string | null {
        if (this.value.address) {
            return this.value.address.fullAddress
        } else {
            return null
        }
    }

    selected(req: CreateServiceRecordPayload | UpdateServiceRecordPayload): void {
        req.branchId = null
        req.projectId = this.value.id;
        req.addressId = null
    }

    constructor(value: ProjectDTO) {
        this.value = value
    }

    public static of(value: ProjectDTO): ProjectLocationOption {
        return new ProjectLocationOption(value)
    }
}

export class AddressLocationOption implements LocationOption {
    value: AddressDTO;

    title(): string {
        return "Address"
    }

    address(): string {
        return this.value.fullAddress!
    }

    selected(req: CreateServiceRecordPayload | UpdateServiceRecordPayload): void {
        req.branchId = null
        req.projectId = null;
        req.addressId = this.value.id
    }

    constructor(value: AddressDTO) {
        this.value = value
    }

    public static of(value: AddressDTO): AddressLocationOption {
        return new AddressLocationOption(value)
    }
}

export type InvoiceStatus = 'VOIDED' | 'COMPLETED' | 'PENDING_PAYMENT' | 'DRAFT'

export class Summary {
    unpaid!: number
    paid!: number
}

export class ServiceInvoice extends InvoiceDTO<ServiceRecordDTO> {
    startDate!: Date | string | null
    endDate!: Date | string | null
}



export class CreateInvoicePayload {
    payeeId: number | null = null;
    payerId: number | null = null;
    serviceLogs: number[] | null = null
    startDate: Date | string | null = null
    endDate: Date | string | null = null
    dueDays = 7
    invoiceDate: Date | string | null = moment().format("yyyy-MM-DD")
}

export class UpdateDraftInvoicePayload {
    id: number | null = null
    payeeId: number | null = null;
    payerId: number | null = null;
    serviceLogs: number[] | null = null
    status: InvoiceStatus | null = null
    dueDays = 7
    invoiceDate: Date | string | null = moment().format("yyyy-MM-DD")
}

export class UpdateInvoicePayload {
    serviceInvoiceId: number | null = null
    payerId: number | null = null
    payeeId: number | null = null
    status: InvoiceStatus | null = null

    constructor(init?: Partial<UpdateInvoicePayload>) {
        Object.assign(this, init);
    }
}

export class ServiceInvoiceFilter implements Filter {
    payerId: number | null = null;
    payeeId: number | null = null;
    participantId: number | null = null;
    status: InvoiceStatus | null = null
    excludeStatus: InvoiceStatus | null = null
    startDate: Date | string | null = null
    endDate: Date | string | null = null
    page = 0;
    size = 20;

    constructor(init?: Partial<ServiceInvoiceFilter>) {
        Object.assign(this, init);
    }
}

export class CreateInvoiceContactPayload {
    name: string | null = null;
    email: string | null = null;
    address: AddressDTO | null = null;
    createdBy: number | null = null;

    constructor(init?: Partial<CreateInvoiceContactPayload>) {
        Object.assign(this, init);
    }
}

export class Locations {
    branches: Array<BranchDto> = []
    projects: Array<ProjectDTO> = []
    addresses: Array<AddressDTO> = []
}